

import { db } from "../../config/firebase";
import {
    CLOUD_FUNCTIONS_API_KEY,
    GCF_GET_OR_CREATE_USER_BY_EMAIL_URL,
    GCF_SEND_EVENT_GENERAL_EMAIL_URL,
} from "../../config/api";
import { collection, getDoc, setDoc, getDocs, doc, deleteDoc, updateDoc, Timestamp } from 'firebase/firestore'

const toMillis = (value) => {
    if (!value) return value;
    if (typeof value === 'number') return value;
    if (value instanceof Date) return value.getTime();
    if (typeof value?.toDate === 'function') return value.toDate().getTime();
    if (typeof value?.seconds === 'number') return value.seconds * 1000;
    return value;
};

const toFirestoreTimestamp = (value, fallback = Timestamp.now()) => {
    if (!value) return fallback;
    if (value instanceof Date) return Timestamp.fromDate(value);
    if (typeof value?.toDate === 'function') return Timestamp.fromDate(value.toDate());
    if (typeof value?.seconds === 'number') return new Timestamp(value.seconds, value.nanoseconds || 0);
    if (typeof value === 'number') return Timestamp.fromDate(new Date(value));
    if (typeof value === 'string') {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
            return Timestamp.fromDate(parsed);
        }
    }
    return fallback;
};

const normalizeUser = (user) => {
    if (!user) return user;
    const createdAt = user.createdAt || user.createDate;
    const demostartdate = user.demostartdate || user.demoStartDate;
    const demoenddate = user.demoenddate || user.demoEndDate;
    return {
        ...user,
        createdAt: toMillis(createdAt),
        updatedAt: toMillis(user.updatedAt),
        pricingDate: toMillis(user.pricingDate),
        demostartdate: toMillis(demostartdate),
        demoenddate: toMillis(demoenddate),
    };
};

const buildServiceError = (error, fallbackMessage) => {
    return {
        message: error?.message || fallbackMessage || 'Beklenmeyen bir hata oluştu.',
        code: error?.code || 'UNKNOWN_ERROR',
    };
};

const getDemoDurationDays = (startValue, endValue) => {
    const start = startValue instanceof Date ? startValue : new Date(startValue);
    const end = endValue instanceof Date ? endValue : new Date(endValue);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
    const diffMs = end.getTime() - start.getTime();
    if (diffMs <= 0) return null;
    return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
};

const formatDemoDateTime = (value) => {
    if (!value) return "";
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const pad = (num) => String(num).padStart(2, "0");
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${day}.${month}.${year} - ${hours}:${minutes}`;
};

const getDisplayName = (user = {}) => {
    const fullName = (user?.fullName || "").toString().trim();
    if (fullName) return fullName;
    const name = (user?.name || "").toString().trim();
    const surName = (user?.surName || "").toString().trim();
    return `${name} ${surName}`.trim();
};

const sendEventGeneralEmail = async ({ to, subject, title, text, detail }) => {
    const email = (to || "").toString().trim().toLowerCase();
    if (!email) {
        return { status: "skipped", reason: "missing_email" };
    }

    const resp = await fetch(GCF_SEND_EVENT_GENERAL_EMAIL_URL, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Api-Key": CLOUD_FUNCTIONS_API_KEY,
        },
        body: JSON.stringify({
            to: email,
            subject,
            title,
            text,
            detail,
        }),
    });

    if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw buildServiceError(new Error(err?.error || "Request failed"), "Mail gönderimi başarısız.");
    }

    await resp.json().catch(() => ({}));
    return { status: "sent" };
};

const sendAccountCreatedEmail = async ({ email, fullName }) => {
    const displayName = (fullName || "").toString().trim() || "Kullanıcı";
    const loginUrl = "https://auth.beetinq.com/";
    const subject = "Beetinq hesabınız oluşturuldu | Your Beetinq account has been created";
    const title = "Beetinq Hesap Bilgilendirmesi";
    const text = [
        `Merhaba ${displayName},`,
        "",
        "Bilgileriniz doğrultusunda Beetinq hesabınız başarıyla oluşturulmuştur.",
        "",
        "Hesabınıza giriş yapmak ve şifrenizi oluşturmak için aşağıdaki bağlantıyı kullanabilirsiniz:",
        "",
        loginUrl,
        "",
        "Giriş yaptıktan sonra profil bilgilerinizi dilediğiniz zaman güncelleyebilirsiniz.",
        "",
        "Herhangi bir sorunuz olursa bizimle iletişime geçebilirsiniz.",
        "",
        "Beetinq Ekibi",
        "",
        "-----",
        "",
        `Hello ${displayName},`,
        "",
        "Your Beetinq account has been successfully created based on the information provided.",
        "",
        "To access your account and set your password, please use the link below:",
        "",
        loginUrl,
        "",
        "After logging in, you may update your profile information at any time.",
        "",
        "If you have any questions, please feel free to contact us.",
        "",
        "Beetinq Team",
    ].join("\n");
    const detail = [
        `<p>Merhaba ${displayName},</p>`,
        "<p>Bilgileriniz doğrultusunda Beetinq hesabınız başarıyla oluşturulmuştur.</p>",
        "<p>Hesabınıza giriş yapmak ve şifrenizi oluşturmak için aşağıdaki bağlantıyı kullanabilirsiniz:</p>",
        `<p><a href="${loginUrl}">${loginUrl}</a></p>`,
        "<p>Giriş yaptıktan sonra profil bilgilerinizi dilediğiniz zaman güncelleyebilirsiniz.</p>",
        "<p>Herhangi bir sorunuz olursa bizimle iletişime geçebilirsiniz.</p>",
        "<p>Beetinq Ekibi</p>",
        "<hr/>",
        `<p>Hello ${displayName},</p>`,
        "<p>Your Beetinq account has been successfully created based on the information provided.</p>",
        "<p>To access your account and set your password, please use the link below:</p>",
        `<p><a href="${loginUrl}">${loginUrl}</a></p>`,
        "<p>After logging in, you may update your profile information at any time.</p>",
        "<p>If you have any questions, please feel free to contact us.</p>",
        "<p>Beetinq Team</p>",
    ].join("");

    return sendEventGeneralEmail({
        to: email,
        subject,
        title,
        text,
        detail,
    });
};

const sendDemoAssignedEmail = async ({ email, fullName, demostartdate, demoenddate }) => {
    const displayName = (fullName || "").toString().trim() || "Kullanıcı";
    const demoStartText = formatDemoDateTime(demostartdate);
    const demoEndText = formatDemoDateTime(demoenddate);
    const demoDays = getDemoDurationDays(demostartdate, demoenddate);
    const demoDaysTextTr = demoDays ? `${demoDays} günlük` : "demo";
    const demoDaysTextEn = demoDays ? `${demoDays}-day` : "demo";
    const subject = "Beetinq demo erişiminiz tanımlandı | Your Beetinq demo access has been activated";
    const title = "Beetinq Demo Bilgilendirmesi";
    const text = [
        `Merhaba ${displayName},`,
        "",
        `Beetinq hesabınıza ${demoDaysTextTr} demo (deneme) erişimi tanımlanmıştır.`,
        "",
        "Deneme süresi detayları aşağıdaki gibidir:",
        demoStartText ? `Başlangıç: ${demoStartText}` : "",
        demoEndText ? `Bitiş: ${demoEndText}` : "",
        "",
        "Belirtilen süre boyunca Beetinq’in tüm demo özelliklerini kullanabilirsiniz.",
        "",
        "Herhangi bir sorunuz olursa bizimle iletişime geçebilirsiniz.",
        "",
        "Beetinq Ekibi",
        "",
        "-----",
        "",
        `Hello ${displayName},`,
        "",
        `A ${demoDaysTextEn} demo access has been activated for your Beetinq account.`,
        "",
        "The demo period details are as follows:",
        demoStartText ? `Start Date: ${demoStartText}` : "",
        demoEndText ? `End Date: ${demoEndText}` : "",
        "",
        "You may use all demo features of Beetinq during this period.",
        "",
        "If you have any questions, please feel free to contact us.",
        "",
        "Beetinq Team",
    ]
        .filter(Boolean)
        .join("\n");
    const detail = [
        `<p>Merhaba ${displayName},</p>`,
        `<p>Beetinq hesabınıza <strong>${demoDaysTextTr} demo (deneme) erişimi</strong> tanımlanmıştır.</p>`,
        "<p>Deneme süresi detayları aşağıdaki gibidir:</p>",
        demoStartText ? `<p>Başlangıç Tarihi: ${demoStartText}</p>` : "",
        demoEndText ? `<p>Bitiş Tarihi: ${demoEndText}</p>` : "",
        "<p>Belirtilen süre boyunca Beetinq’in tüm demo özelliklerini kullanabilirsiniz.</p>",
        "<p>Herhangi bir sorunuz olursa bizimle iletişime geçebilirsiniz.</p>",
        "<p>Beetinq Ekibi</p>",
        "<hr/>",
        `<p>Hello ${displayName},</p>`,
        `<p>A <strong>${demoDaysTextEn} demo access</strong> has been activated for your Beetinq account.</p>`,
        "<p>The demo period details are as follows:</p>",
        demoStartText ? `<p>Start Date: ${demoStartText}</p>` : "",
        demoEndText ? `<p>End Date: ${demoEndText}</p>` : "",
        "<p>You may use all demo features of Beetinq during this period.</p>",
        "<p>If you have any questions, please feel free to contact us.</p>",
        "<p>Beetinq Team</p>",
    ]
        .filter(Boolean)
        .join("");

    return sendEventGeneralEmail({
        to: email,
        subject,
        title,
        text,
        detail,
    });
};


const createUser = async (userId, userPhone, userEmail) => {

    const emailLower = (userEmail || "").toLowerCase();
    const now = Timestamp.now();

    const userData = {
        userId: userId,
        fullName: "",
        phoneNumber: userPhone || "",
        userEmail: emailLower,
        email: emailLower,
        emailLower: emailLower,
        nameLower: "",
        walletId: "",
        name: "",
        surName: "",
        userName: "",
        businessName: "",
        about: "",
        businessPosition: "",
        job: "",
        city: "",
        location: "",
        businessManager: false,
        premium: false,
        state: true,
        isActive: true,
        userPicture: "",
        thumbnail: "",
        role: "",
        businessId: '',
        createDate: now,
        createdAt: now,
        lastLoginDate: now,
        pricingDate: now,
        updatedAt: now,
        demostartdate: null,
        demoenddate: null,
        meetings: [],
        connections: [],
        connectionsReq: [],
        follower: [],
        followUser: [],
        token: "",
        country: "",
        language: "",
        invitations: [],
        meetingTags: [],
        businessInvite: [],
        directMessage: [],
        interests: [],
        business: [],
        blackList: [],
        social: {
            facebook: '',
            instagram: '',
            linkedin: '',
            twitter: '',
            youtube: '',
        },
    };

    const customDocRef = doc(db, 'users', userId);
    await setDoc(customDocRef, userData);
    const docSnap = await getDoc(customDocRef)
    return { ...docSnap.data(), id: customDocRef.id };
}

const getUser = async (userId) => {
    const docRef = await doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
        return { ...docSnap.data(), id: docSnap.id }
    } else {
        return null;
    }
}



const updateUser = async (userData, id) => {
    const docRef = await doc(db, 'users', id);
    await updateDoc(docRef, userData)
    const docSnap = await getDoc(docRef)
    return { ...docSnap.data(), id: docSnap.id };
}

const listUsers = async () => {
    try {
        const colRef = collection(db, 'users');
        const querySnapshot = await getDocs(colRef);
        const now = Date.now();
        const users = querySnapshot.docs.map((docSnap) => {
            const data = docSnap.data();
            const normalized = normalizeUser({
                ...data,
                id: docSnap.id,
            });
            return normalized;
        });
        const expiredPremiumUsers = users.filter((user) => {
            if (!user?.premium) return false;
            if (!user?.demoenddate) return false;
            return Number(user.demoenddate) <= now;
        });
        if (expiredPremiumUsers.length > 0) {
            await Promise.all(
                expiredPremiumUsers.map(async (user) => {
                    const id = user?.id || user?.userId;
                    if (!id) return;
                    try {
                        await updateDoc(doc(db, 'users', id), {
                            premium: false,
                            updatedAt: Timestamp.now(),
                        });
                    } catch (error) {
                        // Keep listing resilient even if one user update fails.
                    }
                })
            );
            expiredPremiumUsers.forEach((user) => {
                user.premium = false;
            });
        }
        users.sort((a, b) => {
            const aDate = a?.createdAt || a?.createDate || 0;
            const bDate = b?.createdAt || b?.createDate || 0;
            return bDate - aDate;
        });
        return users;
    } catch (error) {
        throw buildServiceError(error, 'Kullanıcılar yüklenemedi.');
    }
};

const updateUserActive = async (id, isActive) => {
    try {
        const docRef = doc(db, 'users', id);
        await updateDoc(docRef, {
            isActive,
            updatedAt: Timestamp.now(),
        });
        const docSnap = await getDoc(docRef);
        return normalizeUser({ ...docSnap.data(), id: docSnap.id });
    } catch (error) {
        throw buildServiceError(error, 'Kullanıcı durumu güncellenemedi.');
    }
};

const updateUserById = async (id, userData) => {
    try {
        const docRef = doc(db, 'users', id);
        await updateDoc(docRef, {
            ...userData,
            updatedAt: Timestamp.now(),
        });
        const docSnap = await getDoc(docRef);
        return normalizeUser({ ...docSnap.data(), id: docSnap.id });
    } catch (error) {
        throw buildServiceError(error, 'Kullanıcı güncellenemedi.');
    }
};

const updateUserBusinessId = async (id, businessId) => {
    try {
        const docRef = doc(db, 'users', id);
        await updateDoc(docRef, {
            businessId: businessId || "",
            updatedAt: Timestamp.now(),
        });
        const docSnap = await getDoc(docRef);
        return normalizeUser({ ...docSnap.data(), id: docSnap.id });
    } catch (error) {
        throw buildServiceError(error, 'Kullanıcı firması güncellenemedi.');
    }
};

const assignUserDemo = async (id, { demostartdate, demoenddate }) => {
    try {
        const docRef = doc(db, 'users', id);
        await updateDoc(docRef, {
            demostartdate: toFirestoreTimestamp(demostartdate),
            demoenddate: toFirestoreTimestamp(demoenddate),
            premium: true,
            pricingDate: Timestamp.now(),
            updatedAt: Timestamp.now(),
        });
        const docSnap = await getDoc(docRef);
        const user = normalizeUser({ ...docSnap.data(), id: docSnap.id });

        const recipientEmail = user?.email || user?.userEmail || "";
        const recipientName = getDisplayName(user);
        let mailStatus = "skipped";
        let mailError = "";
        try {
            const result = await sendDemoAssignedEmail({
                email: recipientEmail,
                fullName: recipientName,
                demostartdate,
                demoenddate,
            });
            mailStatus = result?.status || "sent";
        } catch (error) {
            mailStatus = "failed";
            mailError = error?.message || "Mail gönderilemedi.";
        }

        return { ...user, mailStatus, mailError };
    } catch (error) {
        throw buildServiceError(error, 'Demo tanımlaması yapılamadı.');
    }
};

const setUserStandard = async (id) => {
    try {
        const docRef = doc(db, 'users', id);
        await updateDoc(docRef, {
            premium: false,
            demoenddate: Timestamp.now(),
            updatedAt: Timestamp.now(),
        });
        const docSnap = await getDoc(docRef);
        return normalizeUser({ ...docSnap.data(), id: docSnap.id });
    } catch (error) {
        throw buildServiceError(error, 'Kullanıcı standart pakete geçirilemedi.');
    }
};

const deleteUserById = async (id) => {
    try {
        const docRef = doc(db, 'users', id);
        await deleteDoc(docRef);
        return { id };
    } catch (error) {
        throw buildServiceError(error, 'Kullanıcı silinemedi.');
    }
};

const createUserByAdmin = async ({ fullName, name, surName, email, phoneNumber, tempPassword, role, businessId, businessName, businessPosition, city, country }) => {
    try {
        const resp = await fetch(GCF_GET_OR_CREATE_USER_BY_EMAIL_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Api-Key": CLOUD_FUNCTIONS_API_KEY,
            },
            body: JSON.stringify({
                email,
                password: tempPassword,
            }),
        });
        if (!resp.ok) {
            const err = await resp.json().catch(() => ({}));
            throw buildServiceError(new Error(err?.error || "Request failed"), 'Kullanıcı oluşturulamadı.');
        }
        const data = await resp.json();
        const uid = data?.uid;
        if (!uid) return data;

        const docRef = doc(db, 'users', uid);
        const existing = await getDoc(docRef);
        const createdAt = existing.exists() ? existing.data()?.createdAt || existing.data()?.createDate : Timestamp.now();
        const payload = {
            userId: uid,
            fullName: fullName || "",
            phoneNumber: phoneNumber || "",
            userEmail: (email || "").toLowerCase(),
            email: (email || "").toLowerCase(),
            emailLower: (email || "").toLowerCase(),
            nameLower: (fullName || "").toLowerCase(),
            walletId: "",
            name: name || "",
            surName: surName || "",
            userName: "",
            businessName: businessName || "",
            about: "",
            businessPosition: businessPosition || "",
            job: "",
            city: city || "",
            location: "",
            businessManager: false,
            premium: false,
            state: true,
            isActive: true,
            userPicture: "",
            thumbnail: "",
            role: role || "",
            businessId: businessId || "",
            createDate: createdAt,
            createdAt: createdAt,
            lastLoginDate: Timestamp.now(),
            pricingDate: Timestamp.now(),
            updatedAt: Timestamp.now(),
            demostartdate: null,
            demoenddate: null,
            meetings: [],
            connections: [],
            connectionsReq: [],
            follower: [],
            followUser: [],
            token: "",
            country: country || "",
            language: "",
            invitations: [],
            meetingTags: [],
            businessInvite: [],
            directMessage: [],
            interests: [],
            business: [],
            blackList: [],
            social: {
                facebook: '',
                instagram: '',
                linkedin: '',
                twitter: '',
                youtube: '',
            },
        };
        await setDoc(docRef, payload, { merge: true });

        let mailStatus = "skipped";
        let mailError = "";
        try {
            const result = await sendAccountCreatedEmail({
                email: payload.email,
                fullName: payload.fullName,
            });
            mailStatus = result?.status || "sent";
        } catch (error) {
            mailStatus = "failed";
            mailError = error?.message || "Mail gönderilemedi.";
        }

        return { ...data, id: uid, mailStatus, mailError };
    } catch (error) {
        throw buildServiceError(error, 'Kullanıcı oluşturulamadı.');
    }
};

const userService = {
    createUser,
    updateUser,
    getUser,
    listUsers,
    updateUserActive,
    updateUserById,
    deleteUserById,
    createUserByAdmin,
    updateUserBusinessId,
    assignUserDemo,
    setUserStandard,
}

export default userService
